import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Checkbox, Input, Modal, Select, Skeleton, Space, Table, Tag, Typography } from 'antd';
import type { McpAccessAccount, McpToolName } from '@youkuai/contracts';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from './auth.tsx';
import { changeMcpAccess, listMcpAccessAccounts } from './mcp-access-admin-api.ts';
import { ApiRequestError } from './project-api.ts';

const { Paragraph, Text, Title } = Typography;

const recommendedTools: McpToolName[] = [
  'get_my_workbench', 'list_my_tasks', 'list_my_projects', 'get_project', 'get_my_settlement',
];

function errorText(error: unknown): string {
  if (error instanceof ApiRequestError) {
    const messages: Record<string, string> = {
      FORBIDDEN: '当前账号没有系统权限管理权。',
      MCP_ACCESS_INVALID: '授权范围、到期时间或变更原因不合法。',
      MCP_ACCESS_NOT_FOUND: '账号不存在或已删除。',
      MCP_ACCESS_UNAVAILABLE: 'MCP 权限服务当前不可用。',
      VERSION_CONFLICT: '授权已被其他管理员修改，请刷新后重试。',
    };
    return messages[error.code] ?? `操作失败（${error.code}）`;
  }
  return error instanceof Error ? error.message : '操作失败，请稍后重试。';
}

function grantState(account: McpAccessAccount) {
  if (!account.grant?.enabled) return { label: '未开通', color: 'default' };
  if (account.grant.expiresAt && new Date(account.grant.expiresAt).valueOf() <= Date.now()) {
    return { label: '已过期', color: 'warning' };
  }
  return { label: '已开通', color: 'success' };
}

export function McpAccessAdminPage() {
  const { state } = useAuth();
  const token = state.kind === 'ready' ? state.token : '';
  const accountId = state.kind === 'ready' ? state.session.userId : '';
  const client = useQueryClient();
  const [editing, setEditing] = useState<McpAccessAccount | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [allowedTools, setAllowedTools] = useState<McpToolName[]>(recommendedTools);
  const [expiry, setExpiry] = useState<'CURRENT' | 'NEVER' | '30' | '90'>('NEVER');
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const accounts = useQuery({
    queryKey: ['mcp-access-accounts', accountId],
    queryFn: () => listMcpAccessAccounts(token),
    enabled: !!token,
  });
  const mutation = useMutation({
    mutationFn: async () => {
      if (!editing) throw new Error('未选择账号');
      const days = expiry === '30' || expiry === '90' ? Number(expiry) : null;
      const expiresAt = expiry === 'CURRENT' ? editing.grant?.expiresAt ?? null
        : days === null ? null : new Date(Date.now() + days * 86_400_000).toISOString();
      return changeMcpAccess(token, editing.accountId, {
        expectedVersion: editing.grant?.version ?? 0,
        enabled,
        allowedTools,
        expiresAt,
        reason: reason.trim(),
      });
    },
    onSuccess: async (result) => {
      setEditing(null);
      setNotice({ type: 'success', text: result.revokedTokenCount
        ? `授权已更新，并立即吊销 ${result.revokedTokenCount} 个有效令牌。`
        : '授权已更新；收权下一次调用生效，新增工具需重新连接 MCP 后出现。' });
      await client.invalidateQueries({ queryKey: ['mcp-access-accounts', accountId] });
    },
    onError: (error) => setNotice({ type: 'error', text: errorText(error) }),
  });

  function open(account: McpAccessAccount) {
    const currentExpiryIsReusable = account.grant?.expiresAt &&
      new Date(account.grant.expiresAt).valueOf() > Date.now() + 60_000;
    const permissionTools = new Set(account.permissionTools);
    const selectedTools = account.grant
      ? account.grant.allowedTools.filter((tool) => permissionTools.has(tool))
      : recommendedTools.filter((tool) => permissionTools.has(tool));
    setEditing(account);
    setEnabled(account.grant?.enabled ?? false);
    setAllowedTools(selectedTools);
    setExpiry(currentExpiryIsReusable ? 'CURRENT' : 'NEVER');
    setReason('');
    setNotice(null);
  }

  const columns = [
    { title: '账号', key: 'account', render: (_: unknown, row: McpAccessAccount) => <span><b>{row.displayName}</b><br /><Text type="secondary">@{row.username} · {row.personCode}</Text></span> },
    { title: '登录状态', key: 'login', render: (_: unknown, row: McpAccessAccount) => <Space>
      <Tag color={row.accountStatus === 'ACTIVE' ? 'success' : 'default'}>{row.accountStatus === 'ACTIVE' ? '正常' : '已暂停'}</Tag>
      {!row.identityLinked && <Tag color="warning">未绑定身份</Tag>}
    </Space> },
    { title: 'MCP 权限', key: 'grant', render: (_: unknown, row: McpAccessAccount) => {
      const status = grantState(row);
      return <span><Tag color={status.color}>{status.label}</Tag><br /><Text type="secondary">
        {row.grant ? `通道 ${row.grant.allowedTools.length} 个 · 当前有效 ${row.effectiveTools.length} 个${row.grant.expiresAt ? ` · 至 ${new Date(row.grant.expiresAt).toLocaleDateString('zh-CN')}` : ''}` : '需要管理员明确开通'}
      </Text></span>;
    } },
    { title: '操作', key: 'action', render: (_: unknown, row: McpAccessAccount) => <Button onClick={() => open(row)}>配置授权</Button> },
  ];

  return <main className="page-shell customer-shell">
    <div className="section-kicker">MCP ACCESS CONTROL</div>
    <div className="project-heading"><div><Title level={1}>MCP 权限管理</Title>
      <Paragraph>Web 登录权不等于 MCP 访问权。只有在这里明确开通的账号，才能签发令牌和调用白名单工具。</Paragraph>
    </div><Link to="/system/accounts"><Button>返回账号管理</Button></Link></div>

    {notice && <Alert className="workspace-alert" showIcon type={notice.type} title={notice.text} closable onClose={() => setNotice(null)} />}
    <Alert className="workspace-alert" type="info" showIcon title="多层权限同时生效"
      description="当前有效 MCP 工具 = 用户角色能力 ∩ 页面权限 ∩ MCP 通道白名单；调用时还会逐次校验项目成员/负责关系和本人数据范围。MCP 可以收窄权限，不能扩权。" />
    <Card>
      {accounts.isPending && <Skeleton active paragraph={{ rows: 8 }} />}
      {accounts.isError && <Alert type="error" showIcon title="MCP 授权读取失败" description={errorText(accounts.error)} />}
      {accounts.data && <Table rowKey="accountId" pagination={{ pageSize: 20 }} columns={columns} dataSource={accounts.data.items} />}
    </Card>

    <Modal title={editing ? `配置 ${editing.displayName} 的 MCP 权限` : ''} open={!!editing}
      onCancel={() => setEditing(null)} onOk={() => void mutation.mutateAsync()} okText="保存授权" cancelText="取消"
      confirmLoading={mutation.isPending} width={720}
      okButtonProps={{ disabled: reason.trim().length < 2 || (enabled && allowedTools.length === 0) }}>
      {editing && <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Checkbox checked={enabled} disabled={!enabled && (editing.accountStatus !== 'ACTIVE' ||
          !editing.identityLinked || editing.permissionTools.length === 0)}
          onChange={(event) => setEnabled(event.target.checked)}>
          <b>允许该账号使用 MCP</b>
        </Checkbox>
        {(editing.accountStatus !== 'ACTIVE' || !editing.identityLinked) && <Alert type="warning" showIcon
          title="需先启用账号并完成登录身份绑定" />}
        {editing.permissionTools.length === 0 && <Alert type="warning" showIcon
          title="该账号的当前角色能力和页面权限没有可用的 MCP 工具" />}
        {editing.grant?.allowedTools.some((tool) => !editing.permissionTools.includes(tool)) &&
          <Alert type="warning" showIcon title="用户权限已收窄"
            description="原通道白名单中有工具已被角色能力或页面权限禁用，当前不会生效；保存后将从通道白名单移除。" />}
        <div><Text strong>工具白名单</Text><Checkbox.Group value={allowedTools}
          onChange={(values) => setAllowedTools(values as McpToolName[])} style={{ width: '100%', marginTop: 8 }}>
          <Space direction="vertical" style={{ width: '100%' }}>{accounts.data?.tools.map((tool) =>
            <Checkbox key={tool.name} value={tool.name} disabled={!editing.permissionTools.includes(tool.name)}>
            <span><b>{tool.label}</b>{tool.sensitivity === 'SENSITIVE' && <Tag color="warning" style={{ marginLeft: 8 }}>敏感数据</Tag>}<br />
              <Text type="secondary">{tool.description} · 需要 {tool.requiredCapability} / {tool.requiredPage} · {tool.name}</Text></span>
          </Checkbox>)}</Space>
        </Checkbox.Group></div>
        <label><Text strong>授权有效期</Text><Select value={expiry} onChange={setExpiry} style={{ width: '100%', marginTop: 6 }}
          options={[
            ...(editing.grant?.expiresAt && new Date(editing.grant.expiresAt).valueOf() > Date.now() + 60_000
              ? [{ value: 'CURRENT', label: `保持当前到期时间（${new Date(editing.grant.expiresAt).toLocaleString('zh-CN')}）` }]
              : []),
            { value: 'NEVER', label: '持续有效（直到管理员关闭）' },
            { value: '30', label: '从现在起 30 天' }, { value: '90', label: '从现在起 90 天' },
          ]} /></label>
        <label><Text strong>变更原因</Text><Input.TextArea rows={3} maxLength={500} value={reason}
          onChange={(event) => setReason(event.target.value)} placeholder="说明用途、设备或收权原因（必填）" /></label>
        {!enabled && editing.grant?.enabled && <Alert type="warning" showIcon title="关闭后立即吊销该账号的所有有效 MCP 令牌" />}
      </Space>}
    </Modal>
  </main>;
}
